<template>
    <div class="pd-renewal-view" v-if="pd">
        <!-- Progress inside the renewal window -->
        <section-header title="Renewal Progress" icon="fa-bullseye-arrow"/>
        <ui-card class="mb-6">
            <template v-if="pd.profile.declared">
                <div class="renewal-bar">
                    <div class="seg seg-earned" :style="{width: earnedPct + '%'}"></div>
                    <div class="seg seg-progress" :style="{width: progressPct + '%'}"></div>
                </div>
                <div class="renewal-caption mt-2">
                    <strong>{{ renewalCounted | hours }} of {{ pd.profile.target_hours }}</strong> hours toward your renewal on <strong>{{ pd.profile.renewal_on | dateOnly }}</strong>
                    <span v-if="pd.hours.external"> &middot; includes {{ pd.hours.external | hours }} on file elsewhere</span>
                    <span v-if="pd.hours.stem_in_window"> &middot; {{ pd.hours.stem_in_window | hours }} STEM</span>
                    &middot; <router-link :to="settingsRoute">Edit</router-link>
                </div>

                <div class="pace-grid mt-5">
                    <div class="pace">
                        <div class="pace-value">{{ pd.hours.remaining_to_target | hours }}</div>
                        <div class="pace-label">hours still needed</div>
                    </div>
                    <div class="pace">
                        <div class="pace-value">{{ pd.hours.in_progress.potential_hours | hours }}</div>
                        <div class="pace-label">in progress across {{ pd.hours.in_progress.count }} {{ pd.hours.in_progress.count === 1 ? 'course' : 'courses' }}</div>
                    </div>
                    <div class="pace" v-if="pd.pace">
                        <div class="pace-value">{{ pd.pace.years_left }}</div>
                        <div class="pace-label">{{ pd.pace.years_left === 1 ? 'year' : 'years' }} until your renewal</div>
                    </div>
                    <div class="pace" v-if="pd.pace && pd.pace.required_per_year !== null && pd.hours.remaining_to_target > 0">
                        <div class="pace-value">{{ pd.pace.required_per_year | hours }}</div>
                        <div class="pace-label">hours a year from here</div>
                    </div>
                </div>

                <div class="renewal-note mt-4" v-if="pd.hours.remaining_to_target === 0">
                    <i class="fas fa-circle-check has-text-success mr-2"></i>You've reached your renewal target. Hours you keep earning still count toward your next salary column.
                </div>
                <div class="renewal-note mt-4" v-else-if="pd.pace && pd.pace.required_per_year === null">
                    <i class="fas fa-triangle-exclamation has-text-warning mr-2"></i>Your renewal date is nearly here and {{ pd.hours.remaining_to_target | hours }} hours are still needed. A representative can help you plan the fastest route.
                </div>
                <div class="renewal-note mt-4" v-else-if="pd.pace && pd.pace.trailing_per_year < pd.pace.required_per_year">
                    <i class="far fa-circle-info mr-2"></i>You've been earning about {{ pd.pace.trailing_per_year | hours }} hours a year with us. Covering the rest takes about {{ pd.pace.required_per_year | hours }} a year<template v-if="coversRemaining"> &mdash; your courses in progress already cover it</template>.
                </div>
                <div class="renewal-note mt-4" v-else-if="pd.pace">
                    <i class="far fa-circle-info mr-2"></i>At your recent pace of about {{ pd.pace.trailing_per_year | hours }} hours a year, you're on track.
                </div>
            </template>
            <empty-state v-else variant="card" icon="fa-calendar-check" text="Add your certificate renewal date to see the hours you've earned with us inside your renewal window.">
                <b-button type="is-primary" size="is-small" tag="router-link" :to="settingsRoute"><i class="fas fa-plus mr-2"></i>Add Your Renewal Date</b-button>
            </empty-state>
        </ui-card>

        <!-- The ledger -->
        <section-header title="Clock Hour History" icon="fa-award" :count="awards.length"/>
        <ui-card>
            <empty-state v-if="!awards.length" variant="card" icon="fa-award" text="No clock hours awarded yet. Hours appear here as your applications are approved."/>
            <div v-else class="ledger">
                <div class="ledger-row is-head">
                    <div class="col-date">Awarded</div>
                    <div class="col-title">Course or conference</div>
                    <div class="col-hours">Hours</div>
                    <div class="col-window"></div>
                </div>
                <router-link v-for="a in awards" :key="a.source + '-' + a.id" class="ledger-row" :to="awardRoute(a)" :class="{'is-out': pd.profile.declared && !a.in_window}">
                    <div class="col-date">{{ a.completed_at | dateOnly }}</div>
                    <div class="col-title">
                        <div class="title-main">{{ a.item_title || a.title }}</div>
                        <div class="title-sub" v-if="a.item_title && a.title">{{ a.title }}<template v-if="a.stem_units"> &middot; {{ a.stem_units | hours }} STEM</template></div>
                    </div>
                    <div class="col-hours">{{ a.units | hours }}</div>
                    <div class="col-window">
                        <template v-if="pd.profile.declared">
                            <span v-if="a.in_window" class="window-tag is-in"><i class="fas fa-check mr-1"></i>Counts</span>
                            <span v-else class="window-tag">Before window</span>
                        </template>
                    </div>
                </router-link>
                <div class="ledger-row is-foot" v-if="pd.profile.declared && pd.hours.external">
                    <div class="col-date"></div>
                    <div class="col-title">Hours on file elsewhere</div>
                    <div class="col-hours">{{ pd.hours.external | hours }}</div>
                    <div class="col-window"><span class="window-tag is-in"><i class="fas fa-check mr-1"></i>Counts</span></div>
                </div>
                <div class="ledger-row is-total">
                    <div class="col-date"></div>
                    <div class="col-title">{{ pd.profile.declared ? 'Toward this renewal' : 'Total earned with us' }}</div>
                    <div class="col-hours">{{ (pd.profile.declared ? renewalCounted : pd.hours.earned_total) | hours }}</div>
                    <div class="col-window"></div>
                </div>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import EmptyState from "../framework/EmptyState";
    import UiCard from "../UiCard";
    import {formatHours, pdRoute} from "@/utils/pd";

    /**
     * Professional Development > Renewal Progress: the renewal profile
     * edited in place, progress inside the five-year window with pace, and
     * the full ledger of awarded clock hours.
     */
    export default {
        name: "PdRenewalView",
        components: {SectionHeader, EmptyState, UiCard},
        filters: {hours: formatHours},
        computed: {
            ...mapGetters(['pdSummary']),
            pd() {
                return this.pdSummary;
            },
            awards() {
                return this.pd.awards || [];
            },
            settingsRoute() {
                return pdRoute('settings');
            },
            renewalCounted() {
                return (parseFloat(this.pd.hours.counted) || 0) + (parseFloat(this.pd.hours.external) || 0);
            },
            earnedPct() {
                return this.pct(this.renewalCounted);
            },
            progressPct() {
                return Math.min(100 - this.earnedPct, this.pct(this.pd.hours.in_progress.potential_hours));
            },
            coversRemaining() {
                return (parseFloat(this.pd.hours.in_progress.potential_hours) || 0) >= (parseFloat(this.pd.hours.remaining_to_target) || 0);
            }
        },
        methods: {
            pct(hours) {
                const target = this.pd.profile.target_hours || 0;
                if (!target) return 0;
                return Math.max(0, Math.min(100, (parseFloat(hours) || 0) / target * 100));
            },
            awardRoute(a) {
                return a.source === 'registration' ? '/conference/' + a.ref_id : '/course/' + a.ref_id;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .renewal-bar {
        display: flex;
        height: 0.7rem;
        border-radius: 999px;
        background: #ececec;
        overflow: hidden;

        .seg-earned {
            background: #336699;
        }

        .seg-progress {
            background: #9dbbd8;
        }
    }

    .renewal-caption {
        font-size: 0.9rem;
        color: #555;
    }

    .pace-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;

        .pace {
            padding: 0.75rem 1rem;
            border-radius: 8px;
            background: #fafbfc;
            border: 1px solid #eef1f4;
        }

        .pace-value {
            font-size: 1.6rem;
            font-weight: 800;
            line-height: 1.1;
            color: #336699;
        }

        .pace-label {
            font-size: 0.8rem;
            color: #7a7a7a;
            margin-top: 0.15rem;
        }
    }

    .renewal-note {
        font-size: 0.9rem;
        color: #33475b;
    }

    .ledger {
        .ledger-row {
            display: grid;
            // Fixed first column: each row is its own grid, so a content-sized
            // column would line up differently on every row
            grid-template-columns: 11rem minmax(0, 1fr) 5rem 8rem;
            gap: 1.25rem;
            align-items: center;
            padding: 0.6rem 0;
            border-bottom: 1px solid #e8eef4;
            color: #33475b;
            font-size: 0.9rem;

            &.is-head {
                font-size: 0.7rem;
                font-weight: 800;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                color: #7a8a9d;
                padding-top: 0;
            }

            &.is-out {
                color: #8a96a3;

                .title-main {
                    color: #8a96a3;
                }
            }

            &.is-foot {
                color: #5f6b7a;
            }

            &.is-total {
                border-bottom: none;
                font-weight: 800;
                color: #336699;
            }

            &:hover:not(.is-head):not(.is-total):not(.is-foot) {
                background: #f7f9fb;
            }

            .col-date {
                white-space: nowrap;
                font-variant-numeric: tabular-nums;
            }

            .col-hours {
                text-align: right;
                font-variant-numeric: tabular-nums;
                font-weight: 700;
            }

            .col-window {
                text-align: right;
            }

            .title-main {
                font-weight: 600;
                color: #33475b;
            }

            .title-sub {
                font-size: 0.78rem;
                color: #7a8794;
            }

            .window-tag {
                display: inline-block;
                font-size: 0.65rem;
                font-weight: 800;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                padding: 1px 8px;
                border-radius: 999px;
                border: 1px solid #cfd8e2;
                color: #7a8a9d;

                &.is-in {
                    background: #e7f4ec;
                    border-color: #cfe5d6;
                    color: #2e7d4f;
                }
            }

            @media (max-width: 700px) {
                grid-template-columns: minmax(0, 1fr) 4rem;

                .col-date, .col-window {
                    display: none;
                }
            }
        }
    }
</style>
